import { Alert, Button, Input, Modal, Space, Switch } from "antd";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { BaseItem as ZhBaseItem, DetailHeader as ZhDetailHeader, Grid as ZhGrid } from "zhihao-ui-react";

import request from "@/api";
import { useUserStore } from "@/views/stores";
import { ADMIN_PUBLISH_PLAT, NOTIFICATION_TYPE, PUBLISH_STATUS } from "@zhihaoscm/meta";
import type { NotificationItem } from "./types";

export interface NotificationDetailPageProps {
    loadNotification?: (id: number | string) => Promise<NotificationItem>;
    resolveContent?: (content: string) => Promise<string>;
    permissions?: string[];
    publishNotification?: (id: number | string, payload: { publishTime: string }) => Promise<unknown>;
    unpublishNotification?: (id: number | string) => Promise<unknown>;
    revokeNotification?: (id: number | string) => Promise<unknown>;
}

/** 复用 Vue 通知详情接口。 */
export async function loadNotificationDetail(id: number | string): Promise<NotificationItem> {
    const response = await request.service.get<NotificationItem>(`/dataService/admin/notification/${id}`);
    return response.data;
}

/** 沿用 Vue 通知发布接口及 publishTime payload。 */
export async function publishNotificationDetail(
    id: number | string,
    payload: { publishTime: string }
): Promise<unknown> {
    const response = await request.service.put(`/dataService/admin/notification/publish/${id}`, payload);
    return response.data;
}

/** 沿用 Vue 取消发布接口。 */
export async function unpublishNotificationDetail(id: number | string): Promise<unknown> {
    const response = await request.service.put(`/dataService/admin/notification/unpublished/${id}`);
    return response.data;
}

/** 沿用 Vue 撤回接口。 */
export async function revokeNotificationDetail(id: number | string): Promise<unknown> {
    const response = await request.service.put(`/dataService/admin/notification/revoke/${id}`);
    return response.data;
}

/** 复用 Vue editorManage 的媒体地址回填规则，保持通知正文接口契约。 */
export async function resolveNotificationContent(content: string): Promise<string> {
    const rawContent = content.replace(/^==ids=(.*?)\n/, "");
    const mediaPattern = /<(img|video|source)\s+[^>]*src=["']([^"']+)["']/g;
    const mediaSources = Array.from(rawContent.matchAll(mediaPattern)).map(match => match[2]);
    const mediaIds = Array.from(
        new Set(mediaSources.map(source => Number(source.match(/#(\d+)$/)?.[1])).filter(Boolean))
    );
    if (!mediaIds.length) return rawContent;
    const response = await request.service.post<Array<{ id: number; path: string }>>(
        "/dataService/admin/file/ids",
        mediaIds
    );
    let resolved = rawContent;
    mediaSources.forEach(source => {
        const id = Number(source.match(/#(\d+)$/)?.[1]);
        const file = response.data.find(item => item.id === id);
        if (file) resolved = resolved.replaceAll(source, `${file.path}#${file.id}`);
    });
    return resolved;
}

/** 将 Vue 通知详情返回的分类、状态和发布端枚举统一为数字。 */
function normalizeNotificationDetail(item: NotificationItem): NotificationItem {
    return {
        ...item,
        notification: {
            ...item.notification,
            classify: item.notification.classify == null ? undefined : Number(item.notification.classify),
            state: item.notification.state == null ? undefined : Number(item.notification.state),
            publishPlatInfo: item.notification.publishPlatInfo?.map(value => Number(value))
        }
    };
}

/** 按旧 Vue headerPanel 的格式保留通知时间展示。 */
function formatNotificationTime(value?: string): string {
    return value ? value.replace("T", " ").slice(0, 16) : "-";
}

/** 使用旧 Vue detailJson 的发布端元数据生成展示文案。 */
function getPublishPlatformText(values?: number[]): string {
    return (
        (values || [])
            .map(value => ADMIN_PUBLISH_PLAT.getIntroByCode(value))
            .filter((label): label is string => Boolean(label))
            .join("、") || "-"
    );
}

/** 使用旧 Vue 通知分类元数据生成详情文案。 */
function getNotificationTypeText(value?: number): string {
    return NOTIFICATION_TYPE.getIntroByCode(value) || "-";
}

/** 将 Vue 发布状态元数据转换为详情头部文案。 */
function getPublishStatusText(value?: number): string {
    return PUBLISH_STATUS.getIntroByCode(value) || "-";
}

/** 复用 Vue 发布弹窗规则：开启定时发布时默认填充当前时间后五分钟。 */
function getDefaultScheduledPublishValues(): { date: string; time: string } {
    const scheduled = new Date(Date.now() + 5 * 60 * 1000);
    const pad = (value: number) => String(value).padStart(2, "0");
    return {
        date: `${scheduled.getFullYear()}-${pad(scheduled.getMonth() + 1)}-${pad(scheduled.getDate())}`,
        time: `${pad(scheduled.getHours())}:${pad(scheduled.getMinutes())}`
    };
}

export function NotificationDetailPage({
    loadNotification = loadNotificationDetail,
    resolveContent = resolveNotificationContent,
    permissions,
    publishNotification = publishNotificationDetail,
    unpublishNotification = unpublishNotificationDetail,
    revokeNotification = revokeNotificationDetail
}: NotificationDetailPageProps) {
    const location = useLocation();
    const navigate = useNavigate();
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    // 沿用 Vue 全局封禁态，详情页通知写操作保留入口但禁止触发。
    const banButton = useUserStore(state => state.banButton);
    const currentPermissions = permissions ?? storePermissions;
    const id = new URLSearchParams(location.search).get("id");
    const [item, setItem] = useState<NotificationItem | null>(null);
    const [resolvedContent, setResolvedContent] = useState("");
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [operation, setOperation] = useState<"publish" | "unpublish" | "revoke" | null>(null);
    const [timedPublish, setTimedPublish] = useState(false);
    const [publishDate, setPublishDate] = useState("");
    const [publishTime, setPublishTime] = useState("");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!id) return;
        void loadNotification(id)
            .then(async result => {
                const normalized = normalizeNotificationDetail(result);
                // 复用旧 Vue editorManage，移除富文本文件 ID 前缀并回填图片/视频真实地址。
                const content = await resolveContent(normalized.notification.content || "");
                setItem(normalized);
                setResolvedContent(content);
            })
            .catch(() => setErrorMessage("通知详情加载失败"));
    }, [id, loadNotification, resolveContent]);

    const notification = item?.notification;
    const canWrite = currentPermissions.includes("ROLE_SS_NOTICE_W");

    /** 打开 Vue 状态操作确认弹窗并清空定时发布草稿。 */
    const openOperation = (nextOperation: "publish" | "unpublish" | "revoke") => {
        setOperation(nextOperation);
        setTimedPublish(false);
        setPublishDate("");
        setPublishTime("");
        setErrorMessage(null);
    };

    /** 按 Vue LOG_DATE_FORMAT_T 生成定时发布时间。 */
    const getScheduledPublishTime = (): string => {
        if (!publishDate || !publishTime) return "";
        return `${publishDate}T${publishTime.length === 5 ? `${publishTime}:00` : publishTime}`;
    };

    /** 保持 Vue Switch 切换行为，开启时给出可直接提交的默认发布时间。 */
    const handleTimedPublishChange = (enabled: boolean) => {
        setTimedPublish(enabled);
        if (enabled) {
            const defaults = getDefaultScheduledPublishValues();
            setPublishDate(defaults.date);
            setPublishTime(defaults.time);
        } else {
            setPublishDate("");
            setPublishTime("");
        }
    };

    /** 提交通知状态操作并重新加载详情，保持 Vue 操作成功后的返回数据一致。 */
    const submitOperation = async () => {
        if (!id || !operation) return;
        if (operation === "publish" && timedPublish && !getScheduledPublishTime()) {
            setErrorMessage("请选择发布时间");
            return;
        }
        setSubmitting(true);
        try {
            if (operation === "publish") {
                await publishNotification(Number(id), {
                    publishTime: timedPublish ? getScheduledPublishTime() : ""
                });
            } else if (operation === "unpublish") {
                await unpublishNotification(Number(id));
            } else {
                await revokeNotification(Number(id));
            }
            setOperation(null);
            setErrorMessage(null);
            const refreshed = normalizeNotificationDetail(await loadNotification(id));
            setItem(refreshed);
            setResolvedContent(await resolveContent(refreshed.notification.content || ""));
        } catch {
            setErrorMessage("通知状态操作失败");
        } finally {
            setSubmitting(false);
        }
    };

    const operationTitle =
        operation === "publish" ? "是否发布" : operation === "unpublish" ? "是否取消发布" : "是否撤回";
    const operationConfirmLabel =
        operation === "publish" ? "确认发布" : operation === "unpublish" ? "确认取消发布" : "确认撤回";
    return (
        <section aria-label="notification-detail-page" className="flex h-full min-h-full flex-col">
            <div className="section-card">
                {errorMessage ? <Alert message={errorMessage} role="alert" type="error" /> : null}
                {notification ? (
                    <>
                        <ZhDetailHeader
                            leftInfos={[{ label: "创建人", value: item?.user?.name || "-" }]}
                            rightInfos={[
                                ...(notification.state !== PUBLISH_STATUS.DRAFT.code
                                    ? [{ label: "发布时间", value: formatNotificationTime(notification.publishTime) }]
                                    : []),
                                { label: "更新时间", value: formatNotificationTime(notification.editTime) }
                            ]}
                            stateDesc={getPublishStatusText(notification.state)}
                            title="平台通知详情"
                        />
                        <ZhGrid className="mt-6" cols={4} gap={[32, 20]}>
                            <ZhBaseItem label="分类">
                                <span>{getNotificationTypeText(notification.classify)}</span>
                            </ZhBaseItem>
                            <ZhBaseItem label="标题" span={2}>
                                <span>{notification.title || "-"}</span>
                            </ZhBaseItem>
                            <ZhBaseItem label="发布端" span={4}>
                                <span>{getPublishPlatformText(notification.publishPlatInfo)}</span>
                            </ZhBaseItem>
                            <ZhBaseItem label="内容" span={4}>
                                <div
                                    aria-label="通知内容"
                                    className="max-w-3xl [&_img]:max-w-full [&_video]:max-w-full"
                                    // Vue 使用 v-html 展示 editorManage 还原后的可信通知富文本。
                                    dangerouslySetInnerHTML={{ __html: resolvedContent || notification.content || "" }}
                                />
                            </ZhBaseItem>
                        </ZhGrid>
                        <Space className="mt-4">
                            {notification.state === PUBLISH_STATUS.DRAFT.code && canWrite ? (
                                <>
                                    <Button
                                        disabled={banButton}
                                        onClick={() =>
                                            void navigate(`/userOperation/NotificationEdit?id=${notification.id || ""}`)
                                        }
                                    >
                                        编辑
                                    </Button>
                                    <Button
                                        aria-label="发布"
                                        disabled={banButton}
                                        onClick={() => openOperation("publish")}
                                        type="primary"
                                    >
                                        发布
                                    </Button>
                                </>
                            ) : null}
                            {notification.state === PUBLISH_STATUS.PENDING.code && canWrite ? (
                                <Button
                                    aria-label="取消发布"
                                    disabled={banButton}
                                    onClick={() => openOperation("unpublish")}
                                    type="primary"
                                >
                                    取消发布
                                </Button>
                            ) : null}
                            {notification.state === PUBLISH_STATUS.PUBLISHED.code && canWrite ? (
                                <Button
                                    aria-label="撤回"
                                    disabled={banButton}
                                    onClick={() => openOperation("revoke")}
                                    type="primary"
                                >
                                    撤回
                                </Button>
                            ) : null}
                            <Button onClick={() => void navigate("/userOperation/notificationManage")}>返回</Button>
                        </Space>
                    </>
                ) : null}
            </div>
            <Modal
                open={operation !== null}
                title={operationTitle}
                onCancel={() => setOperation(null)}
                footer={
                    <Space>
                        <Button onClick={() => setOperation(null)}>取消</Button>
                        <Button
                            aria-label={operationConfirmLabel}
                            loading={submitting}
                            onClick={() => void submitOperation()}
                            type="primary"
                        >
                            发布
                        </Button>
                    </Space>
                }
            >
                {operation === "publish" ? (
                    <>
                        <label className="flex items-center gap-2">
                            定时发布
                            <Switch aria-label="定时发布" checked={timedPublish} onChange={handleTimedPublishChange} />
                        </label>
                        {timedPublish ? (
                            <Space className="mt-4">
                                <Input
                                    aria-label="发布时间日期"
                                    type="date"
                                    value={publishDate}
                                    onChange={event => setPublishDate(event.target.value)}
                                />
                                <Input
                                    aria-label="发布时间时间"
                                    type="time"
                                    value={publishTime}
                                    onChange={event => setPublishTime(event.target.value)}
                                />
                            </Space>
                        ) : null}
                    </>
                ) : (
                    <div>确认执行“{operation === "unpublish" ? "取消发布" : "撤回"}”操作？</div>
                )}
            </Modal>
        </section>
    );
}

export default NotificationDetailPage;
